import { Box, Container, Divider, FormLabel } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import ButtonNewEmail from "components/ButtonNewEmail/ButtonNewEmail";
import CheckboxSelect from "components/ui/CheckboxSelect/CheckboxSelect";
import CustomRangeDatepicker from "components/ui/Datepicker/CustomRangeDatepicker";
import DateTime from "components/ui/DateTime";
import EmailRowTitle from "components/ui/EmailRowTitle";
import FilterGroup from "components/ui/FilterGroup";
import HeaderBar from "components/ui/HeaderBar";
import SearchInput from "components/ui/SearchInput";
import TagEmailStatus, {
  StatusFromContentDelivery,
} from "components/ui/TagEmailStatus";
import ServerDynamicTableProvider from "contexts/server-table/ServerDynamicTableContext";
import useServerTableUrlState from "hooks/server-table/useServerTableUrlState";
import { useResourceList } from "hooks/useResourceList";
import React from "react";
import { CONTENT_STATUS } from "util/enumValues";
import EmailsList from "../components/EmailsList";
import TagOnlyNewsletter from "../components/TagOnlyNewsletter";

type EmailFilterKey =
  | "statusIn"
  | "contentGroupIdIn"
  | "createdByIdIn"
  | "createdAtFrom"
  | "createdAtTo";

const emailFilterDefaults: Record<EmailFilterKey, string> = {
  statusIn: "",
  contentGroupIdIn: "",
  createdByIdIn: "",
  createdAtFrom: "",
  createdAtTo: "",
};

export default function PageEmails() {
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<EmailFilterKey>({
    filterDefaults: emailFilterDefaults,
  });

  const selectedDateRange = React.useMemo(() => {
    const dates: Date[] = [];
    const fromDate = parseIsoDate(filters.createdAtFrom);
    const toDate = parseIsoDate(filters.createdAtTo);

    if (fromDate) {
      dates.push(fromDate);
    }
    if (toDate) {
      dates.push(toDate);
    }

    return dates;
  }, [filters.createdAtFrom, filters.createdAtTo]);

  const resourceParams = React.useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      q: globalFilter || undefined,
      statusIn: filters.statusIn || undefined,
      contentGroupIdIn: filters.contentGroupIdIn || undefined,
      createdByIdIn: filters.createdByIdIn || undefined,
      createdAtFrom: filters.createdAtFrom || undefined,
      createdAtTo: filters.createdAtTo || undefined,
    }),
    [
      filters.contentGroupIdIn,
      filters.createdAtFrom,
      filters.createdAtTo,
      filters.createdByIdIn,
      filters.statusIn,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
    ],
  );

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: "Modelo",
        accessorKey: "contentGroup",
        accessorFn: (row) => row.contentGroup.id,
        cell: ({ row }) => row.original.contentGroup.name,
      },
      {
        header: "Título",
        accessorKey: "title",
        cell: ({ row }) => (
          <EmailRowTitle title={row.original.title}>
            {row.original.isOnlyNewsletter && <TagOnlyNewsletter />}
          </EmailRowTitle>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        accessorFn: (row) => StatusFromContentDelivery(row),
        cell: ({ row }) => <TagEmailStatus record={row.original} />,
      },
      {
        header: "Destinatários",
        accessorKey: "email.countRecipients",
        enableColumnFilter: false,
      },
      {
        header: "Enviados",
        accessorKey: "email.countSent",
        enableColumnFilter: false,
      },
      {
        header: "Bounce",
        accessorKey: "email.countBounce",
        enableColumnFilter: false,
      },
      {
        header: "Criado por",
        accessorKey: "createdBy",
        accessorFn: (row) => row.createdBy.name,
      },
      {
        header: "Criado em",
        accessorKey: "createdAt",
        cell: (props) => <DateTime date={props.cell.getValue()} />,
      },
      {
        header: "Enviado em",
        accessorKey: "email.createdAt",
        cell: (props) =>
          props.cell.getValue() ? (
            <DateTime date={props.cell.getValue()} />
          ) : null,
      },
    ],
    []
  );

  return (
    <ServerDynamicTableProvider
      columns={columns}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={setPagination}
      onGlobalFilterChange={setGlobalFilter}
      resource={{
        resourceName: "contents",
        params: resourceParams,
      }}
      tableOptions={{
        enableGlobalFilter: true,
      }}
    >
      <HeaderBar
        title="Disparos de Report"
        description="Visualize a gerencie os disparos de e-mail Patri"
      >
        <ButtonNewEmail type="REPORT" />
      </HeaderBar>

      <Divider />

      <Container flexDirection="column">
        <Filters
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          selectedDateRange={selectedDateRange}
          onDateRangeChange={(dates) => {
            const [startDate, endDate] = dates;
            setFilter("createdAtFrom", startDate ? startOfDayIso(startDate) : "");
            setFilter("createdAtTo", endDate ? endOfDayIso(endDate) : "");
          }}
          selectedContentGroupIds={parseCsv(filters.contentGroupIdIn)}
          onContentGroupChange={(ids) => setFilter("contentGroupIdIn", serializeCsv(ids))}
          selectedAuthorIds={parseCsv(filters.createdByIdIn)}
          onAuthorChange={(ids) => setFilter("createdByIdIn", serializeCsv(ids))}
          selectedStatus={parseCsv(filters.statusIn)}
          onStatusChange={(status) => setFilter("statusIn", serializeCsv(status))}
        />
        <EmailsList />
      </Container>
    </ServerDynamicTableProvider>
  );
}

interface FiltersProps {
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  selectedDateRange: Date[];
  onDateRangeChange: (dates: Date[]) => void;
  selectedContentGroupIds: string[];
  onContentGroupChange: (ids: string[]) => void;
  selectedAuthorIds: string[];
  onAuthorChange: (ids: string[]) => void;
  selectedStatus: string[];
  onStatusChange: (status: string[]) => void;
}

function Filters({
  globalFilter,
  onGlobalFilterChange,
  selectedDateRange,
  onDateRangeChange,
  selectedContentGroupIds,
  onContentGroupChange,
  selectedAuthorIds,
  onAuthorChange,
  selectedStatus,
  onStatusChange,
}: FiltersProps) {
  const { data: contentGroups } = useResourceList({
    resourceName: "contentGroups",
    path: "content-groups",
    params: {
      type: "REPORT",
    },
  });

  const { data: authors } = useResourceList({
    resourceName: "authors",
    path: "content/authors",
  });

  const contentGroupOptions =
    contentGroups?.map((c) => ({ value: c.id, label: c.name })) ?? [];
  const authorOptions = authors?.map((c) => ({ value: c.id, label: c.name })) ?? [];
  const statusOptions = Object.entries(CONTENT_STATUS).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  return (
    <FilterGroup justifyContent="space-between" pos="relative">
      <Box flex={1} minW="180px">
        <FormLabel>Buscar</FormLabel>
        <SearchInput
          placeholder={'Buscar por título ou "Por que isso importa?"'}
          value={globalFilter}
          onChange={(value) => onGlobalFilterChange(String(value))}
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por período</FormLabel>
        <CustomRangeDatepicker
          dates={selectedDateRange}
          onDateChange={onDateRangeChange}
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por modelo</FormLabel>
        <CheckboxSelect
          placeholder="Todos os modelos"
          options={contentGroupOptions}
          value={contentGroupOptions.filter((option) =>
            selectedContentGroupIds.includes(option.value),
          )}
          onChange={(value) =>
            onContentGroupChange(value.map((v) => String(v.value)))
          }
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por criador</FormLabel>
        <CheckboxSelect
          placeholder="Todos os criadores"
          options={authorOptions}
          value={authorOptions.filter((option) =>
            selectedAuthorIds.includes(option.value),
          )}
          onChange={(value) =>
            onAuthorChange(value.map((v) => String(v.value)))
          }
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por status</FormLabel>
        <CheckboxSelect
          placeholder="Todos os status"
          options={statusOptions}
          value={statusOptions.filter((option) => selectedStatus.includes(option.value))}
          onChange={(value) =>
            onStatusChange(value.map((v) => String(v.value)))
          }
        />
      </Box>
    </FilterGroup>
  );
}

const parseIsoDate = (date?: string) => {
  if (!date) {
    return null;
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const startOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.toISOString();
};

const endOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate.toISOString();
};

const parseCsv = (value?: string) => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const serializeCsv = (value: string[]) => value.join(",");
